// Recipe Translation Helper Functions

import { 
  RecipeTranslation, 
  RecipeMetadata, 
  LocalizedRecipe, 
  SupportedLanguage,
  CanonicalIngredient,
} from './recipe-types';

// OpenAI API configuration
const OPENAI_API_KEY = process.env.OPENAI_API_KEY;
const OPENAI_MODEL = process.env.OPENAI_MODEL || 'gpt-4o';

// Narration phrases translated for each language
// These are used in buildNarrationScript to ensure audio is fully in target language
const NARRATION_PHRASES: Record<SupportedLanguage, {
  deliciousRecipeFor: string;
  ageRange: string;
  recipeTakes: string;
  toMake: string;
  beforeYouStart: string;
  youWillNeed: string;
  ingredients: string;
  heresWhatYouNeed: string;
  nowLetsMake: string;
  followThese: string;
  steps: string;
  step: string;
  importantSafety: string;
  enjoyYour: string;
}> = {
  en: {
    deliciousRecipeFor: 'A delicious recipe for kids aged',
    ageRange: '',
    recipeTakes: 'This recipe takes about',
    toMake: 'to make',
    beforeYouStart: 'Before you start:',
    youWillNeed: 'You will need',
    ingredients: 'ingredients',
    heresWhatYouNeed: "Here's what you need:",
    nowLetsMake: "Now, let's make",
    followThese: 'Follow these',
    steps: 'steps:',
    step: 'Step',
    importantSafety: 'Important safety reminders:',
    enjoyYour: 'Enjoy your delicious',
  },
  es: {
    deliciousRecipeFor: 'Una deliciosa receta para niños de',
    ageRange: 'años',
    recipeTakes: 'Esta receta toma aproximadamente',
    toMake: 'para hacer',
    beforeYouStart: 'Antes de comenzar:',
    youWillNeed: 'Necesitarás',
    ingredients: 'ingredientes',
    heresWhatYouNeed: 'Esto es lo que necesitas:',
    nowLetsMake: 'Ahora, hagamos',
    followThese: 'Sigue estos',
    steps: 'pasos:',
    step: 'Paso',
    importantSafety: 'Recordatorios de seguridad importantes:',
    enjoyYour: 'Disfruta de tu delicioso',
  },
  fr: {
    deliciousRecipeFor: 'Une délicieuse recette pour les enfants âgés de',
    ageRange: 'ans',
    recipeTakes: 'Cette recette prend environ',
    toMake: 'à faire',
    beforeYouStart: 'Avant de commencer:',
    youWillNeed: 'Vous aurez besoin de',
    ingredients: 'ingrédients',
    heresWhatYouNeed: 'Voici ce dont vous avez besoin:',
    nowLetsMake: 'Maintenant, préparons',
    followThese: 'Suivez ces',
    steps: 'étapes:',
    step: 'Étape',
    importantSafety: 'Rappels de sécurité importants:',
    enjoyYour: 'Profitez de votre délicieux',
  },
  de: {
    deliciousRecipeFor: 'Ein köstliches Rezept für Kinder im Alter von',
    ageRange: 'Jahren',
    recipeTakes: 'Dieses Rezept dauert etwa',
    toMake: 'zu machen',
    beforeYouStart: 'Bevor du anfängst:',
    youWillNeed: 'Du brauchst',
    ingredients: 'Zutaten',
    heresWhatYouNeed: 'Das brauchst du:',
    nowLetsMake: 'Jetzt lasst uns',
    followThese: 'Folge diesen',
    steps: 'Schritten:',
    step: 'Schritt',
    importantSafety: 'Wichtige Sicherheitshinweise:',
    enjoyYour: 'Genieße dein leckeres',
  },
};

/**
 * Translate recipe content from English to target language using OpenAI GPT-4
 */
export async function translateRecipeContent(
  canonical: RecipeTranslation,
  targetLang: SupportedLanguage
): Promise<RecipeTranslation> {
  if (targetLang === 'en') {
    // No translation needed for English
    return canonical;
  }

  const languageNames: Record<SupportedLanguage, string> = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
  };

  const targetLanguageName = languageNames[targetLang];

  const prompt = `You are a professional translator specializing in children's recipes. Translate the following recipe content from English to ${targetLanguageName}. Maintain the friendly, kid-appropriate tone and ensure all food safety information is accurately translated.

IMPORTANT RULES:
- Keep the same number of ingredients and instructions
- Preserve all allergen warnings and safety information
- Use child-friendly language appropriate for ages 3-10
- Maintain the educational and encouraging tone
- Do NOT change quantities, measurements, or cooking times
- Translate ingredient names to their common culinary terms in ${targetLanguageName}

Recipe to translate:

Title: ${canonical.title}

Summary: ${canonical.summary}

Ingredients (names only, translate each):
${canonical.ingredientNames.map((name, i) => `${i + 1}. ${name}`).join('\n')}

Instructions (translate each):
${canonical.instructionTexts.map((text, i) => `${i + 1}. ${text}`).join('\n')}

${canonical.educationalText ? `Educational Text: ${canonical.educationalText}` : ''}

${canonical.beforeYouStart ? `Before You Start: ${canonical.beforeYouStart}` : ''}

${canonical.safetyNotes && canonical.safetyNotes.length > 0 ? `Safety Notes:\n${canonical.safetyNotes.map((note, i) => `${i + 1}. ${note}`).join('\n')}` : ''}

Please respond in JSON format with the following structure:
{
  "title": "translated title",
  "summary": "translated summary",
  "ingredientNames": ["ingredient1", "ingredient2", ...],
  "instructionTexts": ["instruction1", "instruction2", ...],
  "educationalText": "translated educational text or null",
  "beforeYouStart": "translated before you start text or null",
  "safetyNotes": ["safety note 1", "safety note 2", ...] or null
}`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: OPENAI_MODEL,
        messages: [
          {
            role: 'system',
            content: 'You are a professional translator for children\'s recipes. Always respond with valid JSON.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.3, // Lower temperature for more consistent translations
        response_format: { type: 'json_object' },
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();
    const translatedContent = JSON.parse(data.choices[0].message.content);

    // Validate the translation has the same number of items
    if (translatedContent.ingredientNames.length !== canonical.ingredientNames.length) {
      console.warn(`[Translation] Ingredient count mismatch for ${targetLang}. Expected ${canonical.ingredientNames.length}, got ${translatedContent.ingredientNames.length}`);
    }

    if (translatedContent.instructionTexts.length !== canonical.instructionTexts.length) {
      console.warn(`[Translation] Instruction count mismatch for ${targetLang}. Expected ${canonical.instructionTexts.length}, got ${translatedContent.instructionTexts.length}`);
    }

    return {
      title: translatedContent.title,
      summary: translatedContent.summary,
      ingredientNames: translatedContent.ingredientNames,
      instructionTexts: translatedContent.instructionTexts,
      educationalText: translatedContent.educationalText || undefined,
      beforeYouStart: translatedContent.beforeYouStart || undefined,
      safetyNotes: translatedContent.safetyNotes || undefined,
    };
  } catch (error) {
    console.error(`[Translation] Failed to translate recipe to ${targetLang}:`, error);
    throw new Error(`Translation to ${targetLang} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Build narration script for audio generation
 * FIXED: Now uses fully translated phrases to prevent language mixing in ElevenLabs audio
 */
export function buildNarrationScript(
  recipe: RecipeMetadata,
  lang: SupportedLanguage
): string {
  const translation = recipe.translations[lang];
  const canonical = recipe.canonical;
  const phrases = NARRATION_PHRASES[lang];

  const parts: string[] = [];

  // Introduction - now fully translated
  parts.push(`${translation.title}.`);
  parts.push(`${phrases.deliciousRecipeFor} ${canonical.ageRange}${phrases.ageRange ? ' ' + phrases.ageRange : ''}.`);
  
  if (canonical.totalTime) {
    parts.push(`${phrases.recipeTakes} ${canonical.totalTime} ${phrases.toMake}.`);
  }

  // Before you start (if exists) - header translated
  if (translation.beforeYouStart) {
    parts.push(phrases.beforeYouStart);
    parts.push(translation.beforeYouStart);
  }

  // Ingredients section - fully translated
  parts.push(`${phrases.youWillNeed} ${canonical.ingredients.length} ${phrases.ingredients}.`);
  parts.push(phrases.heresWhatYouNeed);
  
  canonical.ingredients.forEach((ing, index) => {
    const name = translation.ingredientNames[index];
    const ingredientText = `${ing.quantity} ${ing.unit} ${name}`.trim();
    parts.push(ingredientText);
  });

  // Instructions section - fully translated
  parts.push(`${phrases.nowLetsMake} ${translation.title}!`);
  parts.push(`${phrases.followThese} ${translation.instructionTexts.length} ${phrases.steps}`);
  
  translation.instructionTexts.forEach((instruction, index) => {
    parts.push(`${phrases.step} ${index + 1}:`);
    parts.push(instruction);
  });

  // Safety notes - header translated
  if (translation.safetyNotes && translation.safetyNotes.length > 0) {
    parts.push(phrases.importantSafety);
    translation.safetyNotes.forEach((note) => {
      parts.push(note);
    });
  }

  // Closing - fully translated
  parts.push(`${phrases.enjoyYour} ${translation.title}!`);

  // Log the generated script for debugging (first 200 chars)
  console.log(`[buildNarrationScript] Generated script for ${lang} (${parts.join(' ').length} chars): "${parts.join(' ').substring(0, 200)}..."`);

  return parts.join(' ');
}

/**
 * Get localized recipe by merging canonical data with specific language translation
 */
export function getRecipeInLanguage(
  metadata: RecipeMetadata,
  lang: SupportedLanguage
): LocalizedRecipe {
  const translation = metadata.translations[lang];
  const canonical = metadata.canonical;

  // Merge ingredients (canonical quantities + localized names)
  const ingredients = canonical.ingredients.map((ing, index) => {
    const name = translation.ingredientNames[index] || 'Unknown ingredient';
    const fullText = `${ing.quantity} ${ing.unit} ${name}`.trim();
    
    return {
      quantity: ing.quantity,
      unit: ing.unit,
      name,
      fullText,
    };
  });

  // Get audio for this language
  const audio = metadata.audio?.[lang];

  return {
    language: lang,
    title: translation.title,
    summary: translation.summary,
    ingredients,
    instructions: translation.instructionTexts,
    educationalText: translation.educationalText,
    beforeYouStart: translation.beforeYouStart,
    safetyNotes: translation.safetyNotes,
    prepTime: canonical.prepTime,
    cookTime: canonical.cookTime,
    totalTime: canonical.totalTime,
    servings: canonical.servings,
    difficulty: canonical.difficulty,
    ageRange: canonical.ageRange,
    allergens: canonical.allergens,
    dietTags: canonical.dietTags,
    nutrition: canonical.nutrition,
    ingredientsImageUrl: metadata.ingredientsImageUrl,
    ingredientsImageAlt: metadata.ingredientsImageAlt,
    stepImages: metadata.stepImages,
    audio,
  };
}

/**
 * Generate translations for all supported languages
 */
export async function generateAllTranslations(
  canonicalTranslation: RecipeTranslation
): Promise<Record<SupportedLanguage, RecipeTranslation>> {
  const translations: Partial<Record<SupportedLanguage, RecipeTranslation>> = {
    en: canonicalTranslation,
  };

  // Generate translations for other languages in parallel
  const translationPromises = [
    translateRecipeContent(canonicalTranslation, 'es'),
    translateRecipeContent(canonicalTranslation, 'fr'),
    translateRecipeContent(canonicalTranslation, 'de'),
  ];

  try {
    const [es, fr, de] = await Promise.all(translationPromises);
    translations.es = es;
    translations.fr = fr;
    translations.de = de;
  } catch (error) {
    console.error('[Translation] Failed to generate all translations:', error);
    throw error;
  }

  return translations as Record<SupportedLanguage, RecipeTranslation>;
}

